import { useRead, ReadState, isCollection, isCreators } from "@/components/LoadingStates";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import { useEffect, useRef, useState } from "react";
import api, { formatError, mutationFailure } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, BookmarkCheck, Trash2, Pencil } from "lucide-react";
import PlatformBadge from "@/components/PlatformBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import RoleBadge from "@/components/RoleBadge";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
// GET /watchlists returns at most 200 documents (to_list(200)) and no total: a full page may be partial.
const WATCHLISTS_READ_LIMIT = 200;

export default function Watchlists() {
  const { canEdit, role } = useAuth();
  const listsRead = useRead("/watchlists", (d) => isCollection(d) && d.every((w) => typeof w.name === "string" && isCreators(w.creators)));
  const creatorsRead = useRead("/creators", isCreators);
  const lists = listsRead.data || [];
  const creators = creatorsRead.data || [];
  const invoker = useRef(null);
  // One form for creation and edition: { mode, id?, title?, name, description, selected, error }. null = closed.
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Synchronous locks: a second click in the same render must not send a second request.
  const savingRef = useRef(false);
  // Each opened dialog gets a ticket; a late completion only closes the dialog it belongs to.
  const formTicket = useRef(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const deletingRef = useRef(false);
  // Bumped on scope change and unmount: a deletion re-read that finishes later publishes nothing.
  const deleteTicket = useRef(0);

  const load = () => listsRead.reload().catch(() => {});
  useEffect(() => {
    formTicket.current += 1; deleteTicket.current += 1; setForm(null); setPendingDelete(null);
    return () => { deleteTicket.current += 1; };
  }, [listsRead.key]);

  const openForm = (next, e) => {
    if (!canEdit) return;
    if (e?.currentTarget) invoker.current = e.currentTarget;
    formTicket.current += 1;
    setForm({ error: null, ...next });
  };
  const openCreate = (e) => openForm({ mode: "create", name: "", description: "", selected: new Set() }, e);
  // Keep every stored creator id, including ones the /creators list does not show, so editing never drops them silently.
  const openEdit = (wl, e) => openForm({ mode: "edit", id: wl.id, title: wl.name, name: wl.name, description: wl.description || "", selected: new Set(wl.creator_ids || (wl.creators || []).map((c) => c.id)) }, e);
  const closeForm = (next) => { if (!next && !savingRef.current) setForm(null); };
  const patchForm = (patch) => setForm((previous) => previous && { ...previous, ...patch });

  const toggle = (id) => setForm((previous) => {
    if (!previous) return previous;
    const s = new Set(previous.selected);
    s.has(id) ? s.delete(id) : s.add(id);
    return { ...previous, selected: s };
  });

  const submit = async () => {
    if (!canEdit || !form || savingRef.current) return;
    if (!form.name.trim()) return patchForm({ error: "Le nom est obligatoire." });
    savingRef.current = true;
    setSaving(true);
    const ticket = formTicket.current;
    const { mode, id, name, description, selected } = form;
    patchForm({ error: null });
    try {
      if (mode === "edit") {
        await api.patch(`/watchlists/${id}`, { name: name.trim(), description, creator_ids: Array.from(selected) });
        toast.success("Watchlist mise à jour");
      } else {
        await api.post("/watchlists", { name, description, creator_ids: Array.from(selected) });
        toast.success("Watchlist created");
      }
      if (formTicket.current === ticket) setForm(null);
      load();
    } catch (err) {
      // The dialog stays open with the draft intact. An unknown outcome triggers a list re-read, never a resend.
      const kind = mutationFailure(err);
      const detail = formatError(err);
      const message = kind === "refused" ? `Le serveur a refusé l'enregistrement : ${detail}`
        : kind === "unknown" ? `Résultat inconnu (${detail}) : ${mode === "edit" ? "la modification a peut-être été enregistrée" : "la watchlist a peut-être été créée"}. Vérifiez la liste avant de réessayer.`
        : detail;
      if (formTicket.current === ticket) patchForm({ error: message });
      toast.error(message);
      if (kind === "unknown") load();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const askDelete = (wl) => { if (canEdit) setPendingDelete({ wl, busy: false, error: null }); };
  const closeDelete = (next) => { if (!next && !deletingRef.current) setPendingDelete(null); };
  const confirmDelete = async (e) => {
    // Keep the dialog open until the server answers; never remove the card before that.
    e.preventDefault();
    if (!canEdit || !pendingDelete || deletingRef.current) return;
    deletingRef.current = true;
    const { wl } = pendingDelete;
    setPendingDelete({ wl, busy: true, error: null });
    const ticket = deleteTicket.current;
    const show = (error) => setPendingDelete((previous) => previous && previous.wl.id === wl.id ? { wl, busy: false, error } : previous);
    try {
      await api.delete(`/watchlists/${wl.id}`);
      setPendingDelete(null);
      toast.success("Deleted");
      load();
    } catch (err) {
      const kind = mutationFailure(err);
      const detail = formatError(err);
      const notFound = err?.response?.status === 404;
      if (kind === "refused" && !notFound) {
        const message = `Le serveur a refusé la suppression : ${detail}. La watchlist n'a pas été supprimée.`;
        show(message);
        toast.error(message);
      } else if (kind === "session") {
        show(detail);
      } else {
        // The card still shown proves nothing about the server: re-read the list instead of resending DELETE.
        // The re-read reports an observed state; it never turns the lost answer into a confirmed deletion.
        const base = notFound
          ? `Watchlist introuvable côté serveur (${detail}) : elle a peut-être déjà été supprimée.`
          : `Résultat inconnu : la suppression a pu aboutir avant l'erreur (${detail}).`;
        let observed;
        try {
          const data = await listsRead.reload();
          if (deleteTicket.current !== ticket) return;
          if (!Array.isArray(data)) observed = `${base} Relecture sans résultat exploitable : l'état reste inconnu.`;
          else if (data.some((item) => item.id === wl.id)) observed = `${base} Relecture : la watchlist figure toujours dans la liste renvoyée par le serveur. Vous pouvez réessayer.`;
          else if (data.length >= WATCHLISTS_READ_LIMIT) observed = `${base} Relecture partielle : le serveur renvoie au plus ${WATCHLISTS_READ_LIMIT} watchlists et celle-ci n'en fait pas partie, ce qui n'établit pas son absence. L'état reste inconnu.`;
          else {
            setPendingDelete(null);
            toast.info(notFound
              ? "Relecture : la watchlist n'apparaît plus dans la liste renvoyée par le serveur."
              : "Relecture : la watchlist n'apparaît plus dans la liste renvoyée par le serveur. C'est l'état observé ; la réponse à la suppression n'a pas été reçue.");
            return;
          }
        } catch {
          if (deleteTicket.current !== ticket) return;
          observed = `${base} La relecture a échoué : l'état reste inconnu. Rechargez la liste avant de réessayer.`;
        }
        show(observed);
        toast.error(observed);
      }
    } finally {
      deletingRef.current = false;
    }
  };

  const editing = form?.mode === "edit";
  const known = new Set(creators.map((c) => c.id));
  const hidden = form ? Array.from(form.selected).filter((id) => !known.has(id)).length : 0;

  return (
    <div className="space-y-6" data-testid="watchlists-page">
      <SectionTabs tabs={SECTION_TABS.creators} activeKey="watchlists" ariaLabel="Creators sections" data-testid="section-tabs-creators" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <BookmarkCheck className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Watchlists</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">Group creators by niche and monitor their performance together.</p>
        </div>
        {canEdit ? (
          <Button data-testid="create-watchlist-button" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New watchlist</Button>
        ) : (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="watchlists-readonly-hint">
            <RoleBadge role={role} /> lecture seule dans ce workspace
          </div>
        )}
      </div>

      {canEdit && (
        <Dialog open={!!form} onOpenChange={closeForm}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="watchlist-form-dialog" onCloseAutoFocus={(e) => { if (invoker.current?.isConnected) { e.preventDefault(); invoker.current.focus(); } }}>
            <DialogHeader>
              <DialogTitle>{editing ? `Modifier « ${form.title} »` : "Create a new watchlist"}</DialogTitle>
              <DialogDescription>{editing ? "La watchlist garde son identité : seuls son nom, sa description et ses créateurs changent." : "Pick creators to include. You can edit this later."}</DialogDescription>
            </DialogHeader>
            {form && (
              <div className="space-y-4" aria-busy={saving}>
                <Input
                  placeholder="Watchlist name (e.g. AI Founders)"
                  aria-label="Nom de la watchlist"
                  value={form.name}
                  maxLength={100}
                  onChange={(e) => patchForm({ name: e.target.value })}
                  data-testid="watchlist-name-input"
                />
                <Textarea
                  placeholder="Optional description…"
                  aria-label="Description"
                  value={form.description}
                  maxLength={500}
                  onChange={(e) => patchForm({ description: e.target.value })}
                  data-testid="watchlist-description-input"
                />
                <div>
                  <div className="text-sm font-medium mb-2">Select creators ({form.selected.size})</div>
                  {hidden > 0 && <p className="text-xs text-muted-foreground mb-2" data-testid="watchlist-hidden-creators">{plural(hidden, "profil")} non affiché{hidden === 1 ? "" : "s"} ici reste{hidden === 1 ? "" : "nt"} dans la liste.</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                    <ReadState read={creatorsRead} scope="watchlists-creators" empty={!creators.length} title="Aucun créateur disponible" primaryCta={{ label: "Consulter Creators", to: "/creators" }}>
                    {creators.map((c) => (
                      <label
                        key={c.id}
                        className="flex items-center gap-3 rounded-md border border-border p-2 cursor-pointer hover:bg-accent"
                        data-testid={`watchlist-creator-option-${c.id}`}
                      >
                        <Checkbox
                          checked={form.selected.has(c.id)}
                          onCheckedChange={() => toggle(c.id)}
                        />
                        <MediaImage src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-8 w-8 rounded-full object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.name} <LiveDemoBadge creator={c} /></div>
                          <div className="text-xs text-muted-foreground truncate">{c.handle}</div>
                        </div>
                        <PlatformBadge platform={c.platform} size="xs" />
                      </label>
                    ))}
                    </ReadState>
                  </div>
                </div>
                {form.error && <p role="alert" className="text-sm text-destructive" data-testid="watchlist-form-error">{form.error}</p>}
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => closeForm(false)} disabled={saving} data-testid="cancel-watchlist-form">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid={editing ? "submit-edit-watchlist" : "submit-create-watchlist"}>
                {saving ? (editing ? "Enregistrement…" : "Création…") : (editing ? "Enregistrer" : "Create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canEdit && (
        <AlertDialog open={!!pendingDelete} onOpenChange={closeDelete}>
          <AlertDialogContent data-testid="delete-watchlist-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer la watchlist « {pendingDelete?.wl.name} » ?</AlertDialogTitle>
              <AlertDialogDescription>
                La liste et sa sélection de {plural(pendingDelete?.wl.creators?.length || 0, "créateur")} seront supprimées définitivement.
                Les créateurs et leurs vidéos restent disponibles dans OutlierPulse.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {pendingDelete?.error && <p role="alert" className="text-sm text-destructive" data-testid="delete-watchlist-error">{pendingDelete.error}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pendingDelete?.busy} data-testid="delete-watchlist-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={pendingDelete?.busy} data-testid="delete-watchlist-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {pendingDelete?.busy ? "Suppression…" : "Supprimer"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <ReadState read={listsRead} scope="watchlists" empty={!lists.length} title="Aucune watchlist disponible" description={canEdit ? "Regroupez des créateurs pour suivre leurs vidéos." : "Un Owner ou Editor peut créer une watchlist."} primaryCta={canEdit ? { label: "New watchlist", onClick: openCreate } : undefined}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {lists.map((wl) => (
            <Card key={wl.id} className="p-4 flex flex-col" data-testid={`watchlist-card-${wl.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Link to={`/watchlists/${wl.id}`} className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{wl.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{wl.description || "No description"}</div>
                </Link>
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => openEdit(wl, e)}
                      aria-label={`Modifier la watchlist « ${wl.name} »`}
                      title="Modifier"
                      className="text-muted-foreground hover:text-foreground"
                      data-testid={`edit-watchlist-${wl.id}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => askDelete(wl)}
                      aria-label={`Supprimer la watchlist « ${wl.name} »`}
                      title="Supprimer"
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-watchlist-${wl.id}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex -space-x-2">
                {(wl.creators || []).slice(0, 5).map((c) => (
                  <MediaImage key={c.id} src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-7 w-7 rounded-full border-2 border-card object-cover" />
                ))}
                {wl.creators?.length > 5 && (
                  <div className="h-7 w-7 rounded-full border-2 border-card bg-muted text-xs font-medium flex items-center justify-center">
                    +{wl.creators.length - 5}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {wl.creators?.length || 0} creator{(wl.creators?.length || 0) === 1 ? "" : "s"}
              </div>
              <Link
                to={`/watchlists/${wl.id}`}
                className="mt-3 text-sm text-primary hover:underline"
                data-testid={`open-watchlist-${wl.id}`}
              >
                Open watchlist<span className="sr-only"> « {wl.name} »</span> <span aria-hidden="true">→</span>
              </Link>
            </Card>
          ))}
        </div>
      </ReadState>
    </div>
  );
}
